import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import RightSidebar from './RightSidebar';
import SettingsModal from './SettingsModal';
import './LayoutWithSidebars.css';

const LayoutWithSidebars = ({ 
    children, 
    documents = [], 
    currentDocumentId = null,
    onDocumentsUpdate,
    toggleFlashcards,
    toggleQuiz,
    toggleChat,
    showBackButton = true
}) => {
    const navigate = useNavigate();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
    const [isRightMobileSidebarOpen, setIsRightMobileSidebarOpen] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [documentsState, setDocumentsState] = useState(documents);
    const [currentDocId, setCurrentDocId] = useState(currentDocumentId);
    
    const sidebarRef = useRef();
    const rightSidebarRef = useRef();
    const { user, loading: authLoading, authInitialized } = useAuth();

    // Update documents state when prop changes
    useEffect(() => {
        setDocumentsState(documents);
    }, [documents]);

    // Update current document ID when prop changes
    useEffect(() => {
        setCurrentDocId(currentDocumentId);
    }, [currentDocumentId]);

    const toggleSidebar = () => {
        // Check if we're on mobile
        if (window.innerWidth <= 768) {
            setIsMobileSidebarOpen(!isMobileSidebarOpen);
        } else {
            setIsSidebarCollapsed(!isSidebarCollapsed);
        }
    };

    const closeMobileSidebar = () => {
        setIsMobileSidebarOpen(false);
    };

    const closeRightMobileSidebar = () => {
        setIsRightMobileSidebarOpen(false);
    };

    const handleDocumentsUpdate = (newDocuments) => {
        console.log('Layout received documents:', newDocuments);
        setDocumentsState(newDocuments);
        if (onDocumentsUpdate) {
            onDocumentsUpdate(newDocuments);
        }
    };

    const toggleRightSidebar = () => {
        setIsRightSidebarCollapsed(!isRightSidebarCollapsed);
    };

    const handleLayoutBack = () => {
        if (typeof window !== 'undefined' && window.history.length > 1) {
            navigate(-1);
            return;
        }
        if (user?.uid) navigate(`/main/${user.uid}`);
        else navigate('/');
    };

    // Show loading state while authentication is being checked
    if (authLoading) {
        return (
            <div className="layout-with-sidebars">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    // Only redirect to landing if not authenticated AND auth is fully initialized
    if (!user && authInitialized) {
        // Check if this is a redirect from Google (URL might contain auth params)
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthParams = urlParams.has('code') || urlParams.has('state') || 
                             window.location.hash.includes('access_token') ||
                             window.location.hash.includes('id_token');
        
        if (hasAuthParams) {
            // We're in the middle of a redirect, show loading instead of redirecting
            return (
                <div className="layout-with-sidebars">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Completing sign-in...</p>
                    </div>
                </div>
            );
        } else {
            // No auth params, safe to redirect to landing
            window.location.href = '/';
            return null;
        }
    }

    return (
        <div className="layout-with-sidebars">
            <Sidebar 
                ref={sidebarRef}
                onCollapse={setIsSidebarCollapsed} 
                toggleFlashcards={toggleFlashcards}
                toggleQuiz={toggleQuiz}
                isCollapsed={isSidebarCollapsed}
                isMobileOpen={isMobileSidebarOpen}
                onMobileClose={closeMobileSidebar}
                documents={documentsState}
            />
            <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isRightSidebarCollapsed ? 'right-sidebar-collapsed' : 'right-sidebar-expanded'}`}>
                <div className="layout-with-sidebars__top-row">
                    {showBackButton ? (
                        <button type="button" className="layout-with-sidebars__back" onClick={handleLayoutBack}>
                            <ArrowLeft size={18} aria-hidden />
                            Back
                        </button>
                    ) : (
                        <span className="layout-with-sidebars__top-spacer" aria-hidden />
                    )}
                    <button
                        type="button"
                        className="layout-with-sidebars__settings-btn"
                        onClick={() => setShowSettingsModal(true)}
                        title="Settings"
                        aria-label="Open settings"
                    >
                        <Settings size={20} aria-hidden />
                    </button>
                </div>
                {React.isValidElement(children) ? React.cloneElement(children, {
                    toggleSidebar,
                    toggleRightSidebar,
                    isRightSidebarCollapsed: isRightSidebarCollapsed
                }) : children}
            </div>
            <RightSidebar 
                ref={rightSidebarRef}
                isCollapsed={isRightSidebarCollapsed}
                onCollapse={setIsRightSidebarCollapsed}
                isMobileOpen={isRightMobileSidebarOpen}
                onMobileClose={closeRightMobileSidebar}
                user={user}
                documents={documentsState}
                currentDocumentId={currentDocId}
                onToggleFlashcards={toggleFlashcards}
                onToggleQuiz={toggleQuiz}
                onToggleChat={toggleChat}
            />
            {showSettingsModal && (
                <SettingsModal
                    onClose={() => setShowSettingsModal(false)}
                    user={user}
                />
            )}
        </div>
    );
};

export default LayoutWithSidebars;
